import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/context/auth-context'
import { useLocation, Link, useNavigate } from 'react-router-dom'
import {
  Home,
  Users,
  Key,
  BarChart3,
  Settings,
  FileText,
  Zap,
  LogOut,
  User,
  ChevronDown,
  ChevronRight
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface DashboardSidebarProps {
  isOpen?: boolean
  onToggle?: () => void
}

interface SubMenuItem {
  id: string
  name: string
  href: string
}

interface NavigationItem {
  id: string
  name: string
  icon: React.ComponentType<{ className?: string }>
  href: string | null
  badge: string | null
  description: string
  submenu: SubMenuItem[] | null
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({ isOpen = true }) => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null)

  const isProvider = user?.userType === 'provider'

  const setActiveItem = (id: string) => {
    // Function to track active menu items
    // Can be used for analytics or other purposes
    return id
  }

  const providerNavigation: NavigationItem[] = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: Home,
      href: '/app/dashboard',
      badge: null,
      description: 'Overview and analytics',
      submenu: null
    },
    {
      id: 'patients',
      name: 'Patients',
      icon: Users,
      href: '/app/patients',
      badge: 'NEW',
      description: 'Access patient records',
      submenu: null
    },
    {
      id: 'staff',
      name: 'Staff Management',
      icon: Key,
      href: '/app/provider/staff',
      badge: null,
      description: 'Manage staff members',
      submenu: null
    },
    {
      id: 'records',
      name: 'Shared Records',
      icon: FileText,
      href: '/app/shared-records',
      badge: null,
      description: 'View patient records',
      submenu: null
    },
    {
      id: 'analytics',
      name: 'Analytics',
      icon: BarChart3,
      href: '/app/analytics',
      badge: 'PRO',
      description: 'Performance insights',
      submenu: null
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: Settings,
      href: '/app/settings',
      badge: null,
      description: 'Account settings',
      submenu: null
    }
  ]

  const individualNavigation: NavigationItem[] = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: Home,
      href: '/app/dashboard',
      badge: null,
      description: 'Overview and health records',
      submenu: null
    },
    {
      id: 'records',
      name: 'My Records',
      icon: FileText,
      href: '/app/records',
      badge: null,
      description: 'Medical and health records',
      submenu: null
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: Settings,
      href: '/app/settings',
      badge: null,
      description: 'Account settings',
      submenu: null
    }
  ]

  const isActive = (href: string) => {
    return location.pathname === href
  }

  const isMenuActive = (menuId: string) => {
    const navigation = isProvider ? providerNavigation : individualNavigation
    const menu = navigation.find(item => item.id === menuId)
    if (!menu?.submenu) return false
    return menu.submenu.some(item => isActive(item.href))
  }

  const toggleMenu = (menuId: string) => {
    setExpandedMenu(expandedMenu === menuId ? null : menuId)
  }

  const handleNavClick = (id: string, href: string | null) => {
    if (href) {
      // Direct navigation for items without submenu
      setActiveItem(id)
      navigate(href)
    } else {
      // Toggle menu for items with submenu
      toggleMenu(id)
    }
  }

  return (
    <div className={`fixed left-0 top-[4.5rem] bottom-0 z-30 transition-all duration-300 ${isOpen ? 'w-64' : 'w-20'} bg-white border-r border-slate-200 shadow-sm`}>
      {/* Content */}
      <div className="relative h-full flex flex-col">

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto [&::-webkit-scrollbar]:hidden hover:[&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300">
          {(isProvider ? providerNavigation : individualNavigation).map((item) => (
            <div key={item.id}>
              {/* Main Menu Item */}
              <div
                onClick={() => handleNavClick(item.id, item.href)}
                className={`group relative block cursor-pointer`}
              >
                <Button
                  variant="ghost"
                  className={`w-full justify-start h-auto px-3 py-2.5 rounded-lg transition-all duration-200 mb-1 ${(item.href && isActive(item.href)) || isMenuActive(item.id)
                    ? 'bg-[#0F172A] text-white font-semibold shadow-sm'
                    : 'text-slate-600 hover:bg-[#0F172A] hover:text-white'
                    }`}
                >
                  <div className="flex items-center space-x-3 w-full">
                    <div className={`h-5 w-5 flex items-center justify-center transition-colors ${(item.href && isActive(item.href)) || isMenuActive(item.id)
                      ? 'text-white'
                      : 'text-slate-500 group-hover:text-white'
                      }`}>
                      <item.icon className="h-5 w-5" />
                    </div>

                    {isOpen && (
                      <div className="flex-1 text-left flex items-center justify-between">
                        <span className="text-sm">{item.name}</span>
                        <div className="flex items-center space-x-2">
                          {item.badge && (
                            <Badge
                              variant="outline"
                              className={`text-[10px] h-5 px-1.5 font-medium border-0 ${item.badge === 'PRO'
                                ? 'bg-[#D64463]/20 text-[#D64463]'
                                : 'bg-emerald-100 text-emerald-700'
                                }`}
                            >
                              {item.badge}
                            </Badge>
                          )}
                          {item.submenu && (
                            <ChevronRight
                              className={`h-4 w-4 transition-transform duration-200 ${expandedMenu === item.id ? 'rotate-90 text-white' : 'text-slate-400 group-hover:text-white'
                                }`}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Button>
              </div>

              {/* Submenu Items */}
              {item.submenu && expandedMenu === item.id && isOpen && (
                <div className="ml-9 border-l-2 border-[#0F172A]/20 pl-2 space-y-1 mb-2">
                  {item.submenu.map((subItem) => (
                    <Link
                      key={subItem.id}
                      to={subItem.href}
                      onClick={() => setActiveItem(subItem.id)}
                      className="group block"
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`w-full justify-start h-auto py-2 px-3 rounded-md transition-all duration-200 ${isActive(subItem.href)
                          ? 'bg-[#0F172A] text-white font-medium shadow-sm'
                          : 'text-slate-500 hover:text-white hover:bg-[#0F172A]'
                          }`}
                      >
                        <span className="text-sm">{subItem.name}</span>
                      </Button>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Bottom Section - Navy Blue Background */}
        <div className="px-3 py-8 bg-[#0F172A] space-y-3 border-t border-slate-200">
          {/* Quick Actions */}
          {isOpen && (
            <div className="space-y-1">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 px-2"
              >
                <Zap className="h-3.5 w-3.5 mr-2" />
                Quick Actions
              </Button>
            </div>
          )}

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={`w-full ${isOpen ? 'justify-start' : 'justify-center'} px-2 py-2 hover:bg-slate-800 border border-transparent hover:border-slate-700 transition-all text-slate-300 hover:text-white`}
              >
                <div className="h-8 w-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                  <User className="h-4 w-4 text-slate-400 group-hover:text-white" />
                </div>
                {isOpen && (
                  <div className="ml-3 text-left flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-200 truncate">
                      {isProvider ? (user?.profile as any)?.institutionName || 'Provider' : user?.profile?.firstName}
                    </div>
                    <div className="text-xs text-slate-400 truncate">
                      {isProvider ? (user?.profile as any)?.institutionType || 'Healthcare' : 'Individual'}
                    </div>
                  </div>
                )}
                {isOpen && <ChevronDown className="h-4 w-4 text-slate-500 ml-auto" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#0F172A] text-white border-slate-700 shadow-xl">
              <DropdownMenuItem asChild className="text-slate-300 focus:bg-slate-800 focus:text-white cursor-pointer">
                <Link to="/app/settings" className="flex items-center">
                  <Settings className="h-4 w-4 mr-2 opacity-70" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-700" />
              <DropdownMenuItem onClick={logout} className="flex items-center text-red-400 focus:text-red-300 focus:bg-red-900/20 cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
}

export default DashboardSidebar